import type { SyntaxNode } from 'web-tree-sitter';
import type { SymbolTag } from '../types.js';
import { createCodeNavYieldState, yieldDuringCodeNavWork } from '../yield-during-work.js';
import { memberAccessRulesForLanguageKey } from './registry.js';
import { isMemberAccessPropertyNameNode, objectNodeFromAccessNode } from './shared.js';

function tagDedupeKey(tag: SymbolTag): string {
  return `${tag.startLine}:${tag.startColumn}:${tag.endLine}:${tag.endColumn}:${tag.role}:${tag.kind}:${tag.name}`;
}

function tagFromReceiverNode(node: SyntaxNode): SymbolTag {
  return {
    name: node.text,
    role: 'ref',
    kind: 'variable',
    startLine: node.startPosition.row,
    startColumn: node.startPosition.column,
    endLine: node.endPosition.row,
    endColumn: node.endPosition.column,
  };
}

function lookupNameNode(
  nameNodes: Map<string, SyntaxNode>,
  tag: SymbolTag,
): SyntaxNode | null {
  const key = `${tag.startLine}:${tag.startColumn}:${tag.name}:${tag.role}:${tag.kind}`;
  if (nameNodes.has(key)) return nameNodes.get(key)!;

  for (const node of nameNodes.values()) {
    if (
      node.text === tag.name &&
      node.startPosition.row === tag.startLine &&
      node.startPosition.column === tag.startColumn
    ) {
      return node;
    }
  }
  return null;
}

/** Drop member-access property/call targets — only the receiver object should be navigable. */
export function filterMemberAccessPropertyTags(
  tags: SymbolTag[],
  nameNodes: Map<string, SyntaxNode>,
  languageKey: string | null,
): SymbolTag[] {
  const rules = memberAccessRulesForLanguageKey(languageKey);
  if (rules.length === 0) return tags;

  return tags.filter((tag) => {
    if (tag.role !== 'ref') return true;
    const nameNode = lookupNameNode(nameNodes, tag);
    if (!nameNode) return true;
    return !isMemberAccessPropertyNameNode(nameNode, rules);
  });
}

/** Tag the object/receiver identifier in member-access expressions when missing from queries. */
export async function augmentMemberReceiverTags(
  tags: SymbolTag[],
  nameNodes: Map<string, SyntaxNode>,
  root: SyntaxNode,
  languageKey: string | null,
): Promise<SymbolTag[]> {
  const rules = memberAccessRulesForLanguageKey(languageKey);
  if (rules.length === 0) return tags;

  const seen = new Set(tags.map(tagDedupeKey));
  const seenRefPositions = new Set(
    tags.filter((tag) => tag.role === 'ref').map((tag) => `${tag.startLine}:${tag.startColumn}:${tag.name}`),
  );
  const out = [...tags];
  const state = createCodeNavYieldState();

  for (const rule of rules) {
    for (const accessNode of root.descendantsOfType(rule.parentType)) {
      await yieldDuringCodeNavWork(state);
      const receiverNode = objectNodeFromAccessNode(accessNode, rule);
      if (!receiverNode) continue;

      const refPositionKey = `${receiverNode.startPosition.row}:${receiverNode.startPosition.column}:${receiverNode.text}`;
      if (seenRefPositions.has(refPositionKey)) continue;

      const tag = tagFromReceiverNode(receiverNode);
      const key = tagDedupeKey(tag);
      if (seen.has(key)) continue;

      seen.add(key);
      seenRefPositions.add(refPositionKey);
      out.push(tag);
      nameNodes.set(`${tag.startLine}:${tag.startColumn}:${tag.name}:${tag.role}:${tag.kind}`, receiverNode);
    }
  }

  return out;
}
